import * as _ from 'lodash';
import { useCallback } from 'react';
import { ControlLabel } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers/plural';
import { LABEL_LENGTH_LIMIT, LABEL_LIMIT_PER_ORG } from '../../../../../shared/labels';
import { isFreeTier } from '../../../../../shared/subscriptions';
import { useDialogContext } from '../../../../contexts/DialogContext';
import { window } from '../../../../globals';
import { BUTTON_TYPE } from '../../../generic/Button/button-constants';
import FormGroup from '../../../generic/Forms/FormGroup';
import MultiTextInput from '../../../generic/Forms/MultiTextInput';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import InfoPanel, { INFO_PANEL_TYPE } from '../../../generic/InfoPanel';
import { DocsLink } from '../../../generic/Link/index';
import Text from '../../../generic/Text';
import type { GeneralSettings } from '../../../../../shared/settings/general';

import { sortLabels } from './hooks/utils';

//  Returns the JSX for the confirmation dialog when deleting a label.
const getDeleteLabelConfirmation = ({
  missingLabel,
  numProjects,
}: {
  missingLabel: string;
  numProjects: number;
}) => (
  <>
    <div className="padding-bottom-sm">
      Are you sure you&apos;d like to delete the &quot;{missingLabel}&quot; label?
    </div>
    {numProjects ? (
      <InfoPanel type={INFO_PANEL_TYPE.WARNING}>
        This label will be removed from the <b>{numProjects}</b> {plural(numProjects, 'project')} using it.
      </InfoPanel>
    ) : (
      <InfoPanel>This label isn&apos;t used in any projects.</InfoPanel>
    )}
    <div className="padding-top-sm">
      <Text.Micro>Note: You must save your organization settings for this change to take effect.</Text.Micro>
    </div>
  </>
);

type Props = {
  settings: GeneralSettings;
  // use the labels in initialSettings to determine if a label is in use
  initialSettings: GeneralSettings;
  onUpdateSettings: (settings: Partial<GeneralSettings>) => void;
  isDisabled: boolean;
};

/**
 * Displays the projects section of the general settings page.
 * This section allows users to define labels that can be applied to projects.
 */
const ProjectsSection: VoidFunctionComponent<Props> = ({
  settings: { labels },
  initialSettings,
  onUpdateSettings,
  isDisabled,
}) => {
  const { onConfirm } = useDialogContext();
  //  This is the function which actually goes and updates the labels in useOrganizationSettings.
  const setLabels = useCallback((newLabels: GeneralSettings['labels']) => onUpdateSettings({ labels: newLabels }), [onUpdateSettings]);
  const onChangeLabels = useCallback(
    (newLabelsRaw: string[]) => {
      //  Because MultiTextInput is treats labels as an array of string, we need to convert it "back" to the GeneralSettings['labels'] type.
      //  We do this by checking if the `label` property matches any of the existing labels. If it does, we use that label. Otherwise, we
      //  create a "new" label object locally.
      const newLabels = newLabelsRaw.map(newLabel => {
        const existingLabel = labels.find(({ label }) => label === newLabel);
        //  Create a "new" label. Doesn't matter about the id and projectCount because they will be overwritten by the server.
        if (!existingLabel) {
          return {
            label: newLabel,
            projectCount: 0,
            id: 0,
          };
        }
        return existingLabel;
      });

      // No change - duplicate was added
      if (newLabels.length === labels.length) {
        return;
      }
      //  Label(s) was/were added
      if (newLabels.length > labels.length) {
        setLabels(newLabels);
        return;
      }

      const missingLabel = _.head(_.difference(labels, newLabels))?.label ?? '';
      const numProjects = initialSettings.labels.find(({ label }) => label === missingLabel)?.projectCount ?? 0;

      onConfirm({
        title: 'Delete Label',
        body: getDeleteLabelConfirmation({ missingLabel, numProjects }),
        onConfirm: () => {
          setLabels(newLabels);
        },
        confirmBtnType: BUTTON_TYPE.ERROR,
        confirmText: 'Delete',
        allowEnterConfirm: true,
      });

      // We have to reassign the labels here to undo the deletion that the MultiTextInput is doing internally.
      // If the user confirms the deletion, then the onConfirm will run and re-delete the value by changing
      // `labels`.
      setLabels([...labels]);
    },
    [labels, initialSettings.labels, onConfirm, setLabels],
  );

  return (
    <>
      <hr />
      <Text.H3>Projects</Text.H3>
      <PaidFeatureGate isRestricted={isFreeTier({
        plan: window.user?.organization?.stripe_plan,
        stripePriceLimit: window.user?.organization?.stripePriceLimit,
      })}>
        <div>
          <FormGroup>
            <ControlLabel>
              Labels ({labels.length}/{LABEL_LIMIT_PER_ORG})
            </ControlLabel>
            <MultiTextInput
              id="orgProjectLabels"
              label="Labels"
              size="medium"
              placeholder="Type in a label and press enter..."
              values={sortLabels(labels.map(({ label }) => label))}
              onChange={onChangeLabels}
              maxLength={LABEL_LENGTH_LIMIT}
              maxItems={LABEL_LIMIT_PER_ORG}
              isDisabled={isDisabled}
              isCaseInsensitive
            />

            {labels.length >= LABEL_LIMIT_PER_ORG && (
              <Text.Micro>
                <span className="org-settings-error">Maximum label count reached.</span>
              </Text.Micro>
            )}

            <Text.Paragraph>
              The labels defined here can then be applied to projects individually in Project Settings.
              Labels must be unique (case-insensitive) and can be up to {LABEL_LENGTH_LIMIT} characters long.
            </Text.Paragraph>
            <Text.Paragraph>
              Labels allow for adding user-defined metadata on projects. They can be used to filter projects on the
              following pages: Projects, Global issues, Global reports. You can learn more&nbsp;
              <u>
                <DocsLink to="/projects-ui-whats-new#labels" openInNewTab>
                  here
                </DocsLink>
              </u>
              .
            </Text.Paragraph>
          </FormGroup>
        </div>
      </PaidFeatureGate>
    </>
  );
};

export default ProjectsSection;
